<script lang="ts" setup>
import { object, string } from 'yup';

import type { ProductDetailsType } from '~/utils/api/product';

defineProps<{
  product: ProductDetailsType;
}>();

const route = useValidatedRoute({ params: object().shape({ slug: string().optional(), id: string().optional() }) });

const isCurrentProduct = (itemUrl: string) => {
  // Получаем текущий URL из роута
  const currentUrl = route.params.slug || route.params.id;
  if (!currentUrl) return false;
  return itemUrl === currentUrl;
};
</script>

<template>
  <section v-if="product.relatedProducts" class="productRelatedProductSection">
    <div class="productRelatedProductSection__container">
      <div class="productRelatedProductSection__content">
        <div class="productRelatedProductSection__list">
          <template v-for="(item, index) in product.relatedProducts" :key="index">
            <NuxtLink
              :to="`/product/${item.url}`"
              :class="[
                'productRelatedProductSection__item',
                {
                  'productRelatedProductSection__item--active': isCurrentProduct(item.url || ''),
                },
              ]"
            >
              <span class="productRelatedProductSection__name">
                {{ item.name }}
              </span>
            </NuxtLink>
          </template>
        </div>
      </div>
    </div>
  </section>
</template>

<style lang="scss">
.productRelatedProductSection {
  display: flex;
  position: relative;

  @include mq('sm') {
    flex-direction: column;
  }

  &__image {
    display: flex;
    width: 50%;
    height: 100svh;
    position: sticky;
    top: 0;
    bottom: 0;
    object-fit: cover;

    @include mq('sm') {
      width: 100%;
      height: auto;
      position: static;
    }
  }

  &__container {
    width: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    padding-top: 50px;

    @include mq('md') {
      width: 100%;
      padding-bottom: 30px;
    }
  }

  &__content {
    width: 100%;
  }

  &__title {
    font-size: 16px;
    line-height: 24px;
    letter-spacing: 0.2px;
    color: #121212;
    margin-top: 0;
    margin-bottom: 24px;

    @include mq('sm') {
      font-size: 24px;
      line-height: 32px;
      margin-bottom: 12px;
    }
  }

  &__description {
    font-size: 14px;
    line-height: 17px;
    letter-spacing: 0.2px;
    color: #121212;
    opacity: 0.4;
    margin-top: 0;
    margin-bottom: 57px;
    overflow-wrap: break-word;
    white-space: pre-wrap;

    @include mq('sm') {
      font-size: 14px;
      line-height: 20px;
      margin-bottom: 52px;
    }
  }

  &__list {
    display: flex;
    gap: 0;

    @include mq('md') {
      flex-direction: column;
    }
  }

  &__item {
    flex: 1;
    padding: 16px 30px;
    min-width: 150px;
    border: 1px solid #e0e0e0;
    background-color: #ffffff;
    cursor: pointer;
    transition: all 0.2s ease;
    text-decoration: none;
    display: flex;
    align-items: center;
    justify-content: center;

    &:first-child {
      border-top-left-radius: 8px;
      border-bottom-left-radius: 8px;

      @include mq('md') {
        border-top-right-radius: 8px;
        border-bottom-left-radius: 0;
      }
    }

    &:last-child {
      border-top-right-radius: 8px;
      border-bottom-right-radius: 8px;

      @include mq('md') {
        border-top-right-radius: 0;
        border-bottom-left-radius: 8px;
      }
    }

    &:not(:first-child) {
      border-left: none;

      @include mq('md') {
        border-left: 1px solid #e0e0e0;
        border-top: none;
      }
    }

    &--active {
      background-color: #000000;
      border-color: #000000;

      .productRelatedProductSection__name {
        color: #ffffff;
      }
    }

    &:hover {
      border-color: #999999;
    }
  }

  &__name {
    font-size: 16px;
    font-weight: 500;
    letter-spacing: 0.5px;
    color: #333333;
    text-transform: uppercase;
    text-align: center;
    white-space: nowrap;

    @include mq('md') {
      white-space: normal;
    }
  }

  &__itemContent {
    white-space: pre-wrap;
  }
}
</style>
